import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import logoBlackSmall from '@/assets/images/logo-black-small.png'
import FlowSvg from './flow-svg'
import AgentsSvg from './agents-svg'
import Seo from '@/components/seo/seo'
import { breadcrumbSchema, caseStudySchema } from '@/components/seo/json-ld'
import { resolveLang } from '@/lib/site'

interface IntegrationItem {
  icon: string
  name: string
  role: string
  tag: string
  tagVariant?: 'crm' | 'msg' | 'survey' | 'llm'
}

interface MetricCard {
  label: string
  value: string
  unit?: string
  delta: string
  accent: 'primary' | 'success' | 'warning' | 'info'
}

export default function CaseJhsf() {
  const { t, i18n } = useTranslation()
  const lang = resolveLang(i18n.resolvedLanguage)
  const currentLang = i18n.resolvedLanguage === 'pt-BR' ? 'PT' : 'EN'
  const toggleLang = () => {
    i18n.changeLanguage(i18n.resolvedLanguage === 'pt-BR' ? 'en' : 'pt-BR')
  }

  const integrationItems = t('caseJhsf.integrations.items', {
    returnObjects: true,
  }) as IntegrationItem[]
  const metricCards = t('caseJhsf.metrics.cards', { returnObjects: true }) as MetricCard[]

  return (
    <div className="theme-light min-h-screen bg-white md:bg-[#cbd5e0] pt-11 md:pt-12">
      <Seo
        title={t('seo.caseJhsf.title') as string}
        description={t('seo.caseJhsf.description') as string}
        path="/case-jhsf"
        type="article"
        jsonLd={[
          breadcrumbSchema(lang, [
            { name: 'Bravic', path: '/' },
            { name: 'Cases', path: '/#cases' },
            { name: 'JHSF', path: '/case-jhsf' },
          ]),
          caseStudySchema(lang, {
            name: t('seo.caseJhsf.title') as string,
            description: t('seo.caseJhsf.description') as string,
            path: '/case-jhsf',
            client: 'JHSF Participações',
          }),
        ]}
      />
      {/* Fixed top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a2e] border-b-2 border-primary">
        <div className="w-full md:w-[210mm] mx-auto px-5 md:px-12 py-2.5 flex justify-between items-center">
          <span className="font-display text-sm font-black tracking-tighter">
            <span className="text-white/90">BRAVIC</span>
            <span className="text-primary">/</span>
          </span>
          <button
            onClick={toggleLang}
            className="flex items-center gap-1.5 text-white/50 hover:text-primary transition-colors cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">language</span>
            <span className="font-display text-[11px] font-semibold tracking-wider">
              {currentLang}
            </span>
          </button>
        </div>
      </div>

      {/* Page 1 — Cover */}
      <DocumentPage>
        <div className="relative bg-[#0f0f1a] flex flex-col justify-between flex-1 overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(rgba(26,188,156,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(26,188,156,0.04) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          />
          <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(0,166,147,0.18)_0%,transparent_70%)] pointer-events-none" />
          <div className="absolute -bottom-16 -left-20 w-[300px] h-[300px] rounded-full bg-[radial-gradient(circle,rgba(0,166,147,0.08)_0%,transparent_70%)] pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary-fixed to-transparent" />

          <div className="relative z-10 flex-1 flex flex-col justify-center px-6 md:px-14 py-12 md:py-16">
            <div className="inline-block bg-primary/15 text-primary text-[11px] font-semibold uppercase tracking-[2px] px-3.5 py-1.5 rounded-full mb-6 w-fit">
              {t('caseJhsf.cover.badge')}
            </div>
            <h1 className="font-display text-3xl md:text-[38px] font-extrabold text-white leading-[1.1] tracking-tight mb-3">
              {t('caseJhsf.cover.title')}{' '}
              <span className="text-primary">{t('caseJhsf.cover.titleAccent')}</span>
              <br />
              {t('caseJhsf.cover.titleLine2')}
            </h1>
            <p className="text-[15px] text-white/60 leading-relaxed max-w-[640px]">
              {t('caseJhsf.cover.description')}
            </p>
          </div>

          {/* Client band */}
          <div className="relative z-10 border-t border-white/10 px-6 md:px-14 py-4 md:py-5 flex items-center gap-5">
            <div className="bg-[#1B2B3A] rounded-xl p-2.5 flex items-center justify-center shrink-0">
              <span className="font-display text-xl font-extrabold text-white tracking-[2px]">
                JHSF
              </span>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-0.5">
                {t('caseJhsf.cover.clientLabel')}
              </p>
              <p className="text-sm font-bold text-white">{t('caseJhsf.cover.clientName')}</p>
              <p className="text-xs text-white/50">{t('caseJhsf.cover.clientTagline')}</p>
            </div>
            <a
              href="https://jhsf.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto text-xs text-primary hover:text-primary-fixed transition-colors hidden md:block"
            >
              {t('caseJhsf.cover.clientLink')}
            </a>
          </div>
          <div className="h-1 bg-gradient-to-r from-primary to-primary-fixed" />
        </div>
      </DocumentPage>

      {/* Page 2 — Fluxograma */}
      <DocumentPage>
        <DocumentHeader label={t('caseJhsf.docLabel')} />
        <div className="px-5 md:px-12 py-6 md:py-10 flex-1">
          <SectionLabel>{t('caseJhsf.flowchart.sectionLabel')}</SectionLabel>
          <SectionTitle title={t('caseJhsf.flowchart.title')} />
          <p className="text-sm text-on-surface-variant leading-relaxed mb-6 max-w-[680px]">
            {t('caseJhsf.flowchart.subtitle')}
          </p>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 md:gap-6 mb-6 text-xs text-on-surface-variant font-medium">
            <div className="flex items-center gap-2.5">
              <span className="w-7 h-[18px] rounded border-2 border-primary bg-white" />
              {t('caseJhsf.flowchart.legendProcess')}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="w-4 h-4 rounded-sm border-2 border-[#F2994A] bg-gradient-to-b from-white to-[#FEF3E6] rotate-45" />
              {t('caseJhsf.flowchart.legendDecision')}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="w-[18px] h-[18px] rounded-full border-2 border-dashed border-[#EB5757] bg-white" />
              {t('caseJhsf.flowchart.legendLoop')}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="font-mono text-[11px] font-bold text-[#27AE60]">— SIM</span>
              {t('caseJhsf.flowchart.legendYes')}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="font-mono text-[11px] font-bold text-[#EB5757]">— NÃO</span>
              {t('caseJhsf.flowchart.legendNo')}
            </div>
          </div>

          {/* Flowchart SVG */}
          <div className="bg-white overflow-x-auto">
            <FlowSvg />
          </div>
        </div>
        <PageFooter page={2} label={t('caseJhsf.footer.page')} />
      </DocumentPage>

      {/* Page 3 — Integrações */}
      <DocumentPage>
        <DocumentHeader label={t('caseJhsf.docLabel')} />
        <div className="px-5 md:px-12 py-6 md:py-10 flex-1">
          <SectionLabel>{t('caseJhsf.integrations.sectionLabel')}</SectionLabel>
          <SectionTitle title={t('caseJhsf.integrations.title')} />
          <p className="text-sm text-on-surface-variant leading-relaxed mb-8 max-w-[680px]">
            {t('caseJhsf.integrations.subtitle')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {integrationItems.map((item, i) => (
              <IntegrationCard key={i} {...item} />
            ))}
          </div>
        </div>
        <PageFooter page={3} label={t('caseJhsf.footer.page')} />
      </DocumentPage>

      {/* Page 4 — Métricas */}
      <DocumentPage>
        <DocumentHeader label={t('caseJhsf.docLabel')} />
        <div className="px-5 md:px-12 py-6 md:py-10 flex-1">
          <SectionLabel>{t('caseJhsf.metrics.sectionLabel')}</SectionLabel>
          <SectionTitle title={t('caseJhsf.metrics.title')} />
          <p className="text-sm text-on-surface-variant leading-relaxed mb-8 max-w-[680px]">
            {t('caseJhsf.metrics.subtitle')}
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {metricCards.map((card, i) => (
              <MetricCardComponent key={i} {...card} />
            ))}
          </div>
        </div>
        <PageFooter page={4} label={t('caseJhsf.footer.page')} />
      </DocumentPage>

      {/* Page 5 — Arquitetura de Agentes */}
      <DocumentPage>
        <DocumentHeader label={t('caseJhsf.docLabel')} />
        <div className="px-5 md:px-12 py-6 md:py-10 flex-1">
          <SectionLabel>{t('caseJhsf.agents.sectionLabel')}</SectionLabel>
          <SectionTitle title={t('caseJhsf.agents.title')} />
          <p className="text-sm text-on-surface-variant leading-relaxed mb-8 max-w-[680px]">
            {t('caseJhsf.agents.subtitle')}
          </p>

          <div className="bg-white overflow-x-auto pb-6">
            <AgentsSvg />
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-6 mt-6 text-xs font-medium text-on-surface-variant">
            <div className="flex items-center gap-2.5">
              <span className="w-5 h-5 rounded bg-[#1B2B3A]" />
              {t('caseJhsf.agents.legendOrchestrator')}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="w-5 h-5 rounded bg-primary-container" />
              {t('caseJhsf.agents.legendDirectors')}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="w-5 h-5 rounded bg-white border-2 border-primary-container" />
              {t('caseJhsf.agents.legendSubAgents')}
            </div>
          </div>

          {/* Footer brand */}
          <div className="mt-10 flex items-center gap-4 pt-6 border-t border-[#e2e8f0]">
            <img src={logoBlackSmall} alt="Bravic" className="h-6" />
            <div>
              <p className="text-[10px] text-on-surface-variant uppercase tracking-widest">
                {t('caseJhsf.footer.creditLabel')}
              </p>
              <p className="text-xs font-semibold text-[#1a1a2e]">
                {t('caseJhsf.footer.creditText')}
              </p>
            </div>
            <span className="ml-auto text-[10px] font-mono text-on-surface-variant">
              {t('caseJhsf.footer.reportVersion')}
            </span>
          </div>
        </div>
        <PageFooter page={5} label={t('caseJhsf.footer.page')} />
      </DocumentPage>

      <div className="h-8" />
    </div>
  )
}

function DocumentPage({ children }: { children: ReactNode }) {
  return (
    <section className="relative bg-white md:shadow-[0_8px_40px_rgba(0,0,0,0.15)] w-full md:w-[210mm] md:min-h-[297mm] mx-auto md:my-8 overflow-hidden flex flex-col border-b border-[#e2e8f0] md:border-none md:rounded-sm">
      {children}
    </section>
  )
}

function DocumentHeader({ label }: { label: string }) {
  return (
    <div className="bg-[#1a1a2e] px-5 md:px-12 py-2.5 md:py-3 flex justify-between items-center border-b-2 border-primary">
      <span className="text-[9px] md:text-[10px] font-bold text-white/70 tracking-widest uppercase">
        {label}
      </span>
      <span className="font-display text-xs md:text-sm font-black tracking-tighter">
        <span className="text-white/90">BRAVIC</span>
        <span className="text-primary">/</span>
      </span>
    </div>
  )
}

function PageFooter({ page, label }: { page: number; label: string }) {
  return (
    <div className="px-5 md:px-12 py-2.5 md:py-3 border-t border-[#e2e8f0] flex justify-between items-center text-[9px] text-[#718096]">
      <img src={logoBlackSmall} alt="Bravic" className="h-4" />
      <span>
        {label} {page}
      </span>
    </div>
  )
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <span className="inline-block text-[11px] font-semibold uppercase tracking-[2px] text-primary-container mb-2">
      {children}
    </span>
  )
}

function SectionTitle({ title }: { title: string }) {
  return (
    <div className="mb-4">
      <div className="w-10 md:w-12 h-1 bg-primary rounded-full mb-3" />
      <h2 className="font-display text-xl md:text-[28px] font-bold tracking-tight text-[#1a1a2e]">
        {title}
      </h2>
    </div>
  )
}

function IntegrationCard({
  icon,
  name,
  role,
  tag,
  tagVariant,
}: {
  icon: string
  name: string
  role: string
  tag: string
  tagVariant?: string
}) {
  const tagColors =
    tagVariant === 'llm'
      ? 'bg-purple-100 text-purple-700'
      : tagVariant === 'msg'
        ? 'bg-emerald-100 text-emerald-700'
        : tagVariant === 'survey'
          ? 'bg-amber-100 text-amber-700'
          : tagVariant === 'crm'
            ? 'bg-sky-100 text-sky-700'
            : 'bg-primary/10 text-primary-container'

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#f4f6f8] flex items-center justify-center shrink-0">
          <span className="material-symbols-outlined text-primary-container text-lg">{icon}</span>
        </div>
        <h4 className="text-sm font-bold text-[#1a1a2e]">{name}</h4>
      </div>
      <p className="text-[11px] text-on-surface-variant leading-relaxed flex-1">{role}</p>
      <span
        className={`text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full w-fit ${tagColors}`}
      >
        {tag}
      </span>
    </div>
  )
}

function MetricCardComponent({
  label,
  value,
  unit,
  delta,
  accent,
}: {
  label: string
  value: string
  unit?: string
  delta: string
  accent: 'primary' | 'success' | 'warning' | 'info'
}) {
  const accentColors = {
    primary: 'border-l-primary',
    success: 'border-l-emerald-500',
    warning: 'border-l-amber-500',
    info: 'border-l-sky-500',
  }
  const valueColors = {
    primary: 'text-[#1a1a2e]',
    success: 'text-emerald-600',
    warning: 'text-amber-600',
    info: 'text-[#1a1a2e]',
  }

  return (
    <div
      className={`bg-white border border-[#e2e8f0] rounded-lg p-4 border-l-4 ${accentColors[accent]}`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[1.5px] text-on-surface-variant mb-3">
        {label}
      </p>
      <div
        className={`font-display font-extrabold tracking-tight leading-none mb-2 text-2xl md:text-[32px] ${valueColors[accent]}`}
      >
        {value}
        {unit && <span className="text-sm font-semibold text-on-surface-variant ml-1">{unit}</span>}
      </div>
      <p className="text-[11px] text-on-surface-variant">{delta}</p>
    </div>
  )
}
